import ContactSupportModal from '@/components/contact-support-modal';
import MobileSideMenu from '@/components/mobile/mobile-side-menu';
import { useEscapeClosableDropdown } from '@/hooks/useEscapeClosableDropdown';
import { MyMemoriesPanel } from '@/pages/profile/my-memories-panel';
import { UserProfilePanel } from '@/pages/profile/user-profile-panel';
import { useAuthStore } from '@/store/use-auth-store';
import { useUserStore } from '@/store/use-user-store';
import {
  HAS_ADMIN_PERMISSIONS,
  HAS_SUPER_ADMIN_PERMISSIONS,
} from '@/utils/constants';
import { useProfileDisplayName } from '@/utils/profile-variables';
import {
  getLoginRedirectPath,
  isSoulMapConsumerSession,
  shouldMarkSoulMapPostLogout,
} from '@/utils/soulmap-auth';
import { Icon } from '@iconify/react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Spinner,
  cn,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import logo from 'Src/assets/logo.png';
import { useGetUserPermissions } from 'Src/hooks/queries/permissions';
import { useAuthQuery } from 'Src/hooks/useAuthQuery';
import { useMediaQuery } from 'usehooks-ts';

const TERMS_OF_USE_URL = 'https://mentora.institute/termsofuse-mentora/';

interface Permission {
  codename: string;
  name: string;
}

const Header = () => {
  const { logout } = useAuthQuery();
  const { data: permissions, isSuccess, isLoading } = useGetUserPermissions();
  const { user, setHasAdminPermissions, setHasSuperAdminPermissions } =
    useUserStore();
  const { fullName, isReady } = useProfileDisplayName();
  const displayName =
    isReady && fullName
      ? fullName
      : user
        ? `${user.first_name} ${user.last_name}`.trim()
        : 'Guest User';
  const isSso = useAuthStore((state) => state.authMethod === 'sso');
  const [isScrolled] = useState(false);
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);
  const [isMemoriesPanelOpen, setIsMemoriesPanelOpen] = useState(false);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const userDropdown = useEscapeClosableDropdown();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const hasAdminPermissions = permissions?.results.find(
    (p: Permission) => p.codename === HAS_ADMIN_PERMISSIONS
  );
  const isMobile = useMediaQuery('(max-width: 768px)');
  const soulMapConsumer = isSoulMapConsumerSession();

  useEffect(() => {
    if (isSuccess && permissions) {
      const hasAdmin = !!permissions.results.find(
        (p: Permission) => p.codename === HAS_ADMIN_PERMISSIONS
      );
      const hasSuperAdmin = !!permissions.results.find(
        (p: Permission) => p.codename === HAS_SUPER_ADMIN_PERMISSIONS
      );
      setHasAdminPermissions(hasAdmin);
      setHasSuperAdminPermissions(hasSuperAdmin);
    }
  }, [permissions, isSuccess]);

  const handleLogout = async () => {
    // SSO logout (outside Soul Map) hands the page to Auth0, which redirects.
    const redirectsToAuth0 = isSso && !soulMapConsumer;
    await logout.mutateAsync();
    if (redirectsToAuth0) return;
    const loginPath = shouldMarkSoulMapPostLogout(window.location.pathname)
      ? getLoginRedirectPath('/soulmap-login')
      : '/login';
    navigate(loginPath, { replace: true });
  };

  return (
    <header
      className={cn(
        'md:sticky md:top-0 fixed top-0 flex h-16 w-full items-center justify-between bg-white md:bg-white/80  px-4 md:px-6 z-[10] border-b border-gray-100 shadow-[0_2px_4px_rgba(0,0,0,0.04)] shrink-0 transition-all duration-300 ease-in-out',
        isScrolled && 'shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
      )}
      style={{
        // Add safe area padding for iOS notch/dynamic island
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Left section - Mobile hamburger menu */}
      <div className="flex-1 flex items-center">
        {isMobile && (
          <Button
            isIconOnly
            variant="light"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="-ml-2"
          >
            <Icon icon="lucide:menu" className="w-6 h-6 text-[#ff6b6b]" />
          </Button>
        )}
      </div>

      {/* Center section - Logo and Title */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-2">
          <img src={logo} alt="LiFT Logo" className="h-6 w-6" />
          <span className="text-[18px] md:text-[26px] whitespace-nowrap">
            <span className="text-[#1C365D]">Mentora</span>{' '}
            <span className="font-bold">LiFT</span>
          </span>
        </div>
      </div>
      {/* Tenant switcher */}

      {/* Right section - Desktop user dropdown */}
      <div className="flex flex-1 items-center justify-end">
        <Dropdown {...userDropdown.dropdownProps}>
          <DropdownTrigger>
            <Button
              {...userDropdown.triggerProps}
              className="justify-start text-default-500 data-[hover=true]:text-foreground pr-0 min-w-0 gap-0 sm:gap-2"
              variant="light"
              endContent={
                isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Icon
                    className="flex-none text-default-500"
                    icon="lsicon:down-outline"
                    width={24}
                  />
                )
              }
              isDisabled={isLoading}
            >
              <div className="flex items-center gap-2">
                <Icon
                  className="flex-none text-default-500"
                  icon="mdi-light:account"
                  width={24}
                />
                {!isMobile && (
                  <div className="flex max-w-full flex-col">
                    <p className="text-small font-medium text-foreground text-start">
                      {displayName}
                    </p>
                  </div>
                )}
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="User actions">
            <DropdownSection showDivider aria-label="User">
              <DropdownItem key="user-info" className="py-2" isReadOnly>
                <div className="flex flex-col items-start">
                  <span className="text-small font-medium truncate max-w-[150px]">
                    {displayName}
                  </span>
                  <span className="text-xs text-default-500 truncate max-w-[150px]">
                    {user?.email}
                  </span>
                </div>
              </DropdownItem>
            </DropdownSection>
            <DropdownSection showDivider aria-label="User Actions">
              {hasAdminPermissions && (
                <DropdownItem
                  key="admin"
                  className="py-2"
                  onPress={() => navigate(`/${tenantSlug}/agents`)}
                >
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi-shield-account" width={20} />
                    <span>Admin</span>
                  </div>
                </DropdownItem>
              )}
              <DropdownItem
                key="profile"
                className="py-2"
                onPress={() => setIsProfilePanelOpen(true)}
              >
                <div className="flex items-center gap-2">
                  <Icon icon="mdi:account-edit" width={20} />
                  <span>User Profile</span>
                </div>
              </DropdownItem>
              <DropdownItem
                key="practice-history"
                className="py-2"
                onPress={() =>
                  navigate(`/${tenantSlug}/practices/users/${user?.id}`)
                }
              >
                <div className="flex items-center gap-2">
                  <Icon
                    icon="material-symbols:work-history-outline"
                    width={20}
                  />
                  Practice History
                </div>
              </DropdownItem>
            </DropdownSection>
            {soulMapConsumer ? (
              <DropdownSection showDivider aria-label="User Settings">
                <DropdownItem
                  key="terms"
                  className="py-2"
                  onPress={() =>
                    window.open(
                      TERMS_OF_USE_URL,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:file-document-outline" width={20} />
                    <span>Terms of Use</span>
                  </div>
                </DropdownItem>
              </DropdownSection>
            ) : (
              <DropdownSection showDivider aria-label="User Settings">
                <DropdownItem
                  key="terms"
                  className="py-2"
                  onPress={() =>
                    window.open(
                      TERMS_OF_USE_URL,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:file-document-outline" width={20} />
                    <span>Terms of Use</span>
                  </div>
                </DropdownItem>
                <DropdownItem
                  key="contact"
                  className="py-2"
                  onPress={() => setIsContactSupportOpen(true)}
                >
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:email-outline" width={20} />
                    <span>Contact Support</span>
                  </div>
                </DropdownItem>
                <DropdownItem
                  key="change-password"
                  onPress={() => {
                    if (isSso) {
                      setIsContactSupportOpen(true);
                    } else {
                      navigate(`/${tenantSlug}/home/change-password`);
                    }
                  }}
                  startContent={<Icon icon="ic:baseline-lock" width={20} />}
                >
                  Change Password
                </DropdownItem>
              </DropdownSection>
            )}
            <DropdownItem
              key="logout"
              className="text-danger"
              onPress={handleLogout}
              startContent={<Icon icon="ic:baseline-logout" width={20} />}
            >
              Log Out
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>

      <UserProfilePanel
        isOpen={isProfilePanelOpen}
        onClose={() => setIsProfilePanelOpen(false)}
        onOpenMemories={() => setIsMemoriesPanelOpen(true)}
      />
      <MyMemoriesPanel
        isOpen={isMemoriesPanelOpen}
        onClose={() => setIsMemoriesPanelOpen(false)}
        onBackToProfile={() => {
          setIsMemoriesPanelOpen(false);
          setIsProfilePanelOpen(true);
        }}
      />
      <ContactSupportModal
        isOpen={isContactSupportOpen}
        onClose={() => setIsContactSupportOpen(false)}
        title={isSso ? 'Password Change Restricted' : 'Need Assistance?'}
        message={
          isSso
            ? "Password changes for Single Sign-On (SSO) user is managed by your company's IT team. Please reach out to your IT department if you need help updating your password"
            : 'Please reach out to us at programsupport@mentora.institute if you have any questions.'
        }
      />

      {/* Mobile Side Menu - uses portal to render at body level */}
      <MobileSideMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </header>
  );
};

export default Header;
